import { Store } from '../store/types';
import {
  CHECK_PHONE_VERIFY_REQUEST,
  CREATE_LOYALTY_REQUEST,
  GENERATE_CODE_REQUEST,
  REGISTRATION_MODAL,
  VERIFY_CODE_REQUEST,
} from 'actions/loyalty-registration-modal';
import { checkPhoneVerify } from '../api/check-phone-verify';
import { createLoyaltyRequest } from '../api/create-loyalty-request';
import { verificationCodeGenerate } from '../api/verification-code-generate';
import { verificationCodeVerify } from '../api/verification-code-verify';
import { ELoyaltyCustomEvents } from 'components/header/hooks';
import i18n from 'i18next';

export enum ERegistrationStep {
  CODE_GENERATE = 'CODE_GENERATE',
  CODE_SEND = 'CODE_SEND',
  CARD_CHOOSE = 'CARD_CHOOSE',
  SUCCESS = 'SUCCESS',
}

const defaultLoyaltyRegistrationModalStore = {
  show: false,
  step: ERegistrationStep.CARD_CHOOSE,
  checkPhoneVerifyRequest: {
    loading: false,
    error: null,
  },
  createLoyaltyRequest: {
    loading: false,
    error: null,
  },
  generateCodeRequest: {
    loading: false,
    error: null,
  },
  verifyCodeRequest: {
    loading: false,
    error: null,
  },
  keycardChooseStep: {
    marketingConsent: false,
    keycardType: null,
  },
  successStep: {
    keycardType: null,
    cardNumber: null,
  },
  codeVerifyStep: {
    phoneNumber: null,
    forcedPhoneNumber: null,
  },
};

export const loyaltyRegistrationModalReducer = (store: Store) => {
  store.on('@init', (state) => ({
    ...state,
    loyaltyRegistrationModal: defaultLoyaltyRegistrationModalStore,
  }));

  store.on(REGISTRATION_MODAL.resetStore, (state) => ({
    ...state,
    loyaltyRegistrationModal: defaultLoyaltyRegistrationModalStore,
  }));

  store.on(CHECK_PHONE_VERIFY_REQUEST.loading, (state) => ({
    ...state,
    loyaltyRegistrationModal: {
      ...state.loyaltyRegistrationModal,
      checkPhoneVerifyRequest: {
        ...state.loyaltyRegistrationModal.checkPhoneVerifyRequest,
        loading: true,
      },
    },
  }));

  store.on(CHECK_PHONE_VERIFY_REQUEST.success, (state) => ({
    ...state,
    loyaltyRegistrationModal: {
      ...state.loyaltyRegistrationModal,
      checkPhoneVerifyRequest: {
        ...state.loyaltyRegistrationModal.checkPhoneVerifyRequest,
        loading: false,
      },
    },
  }));

  store.on(CHECK_PHONE_VERIFY_REQUEST.failure, (state, data) => ({
    ...state,
    loyaltyRegistrationModal: {
      ...state.loyaltyRegistrationModal,
      checkPhoneVerifyRequest: {
        ...state.loyaltyRegistrationModal.checkPhoneVerifyRequest,
        loading: false,
        error: data,
      },
    },
  }));

  store.on(CREATE_LOYALTY_REQUEST.loading, (state) => ({
    ...state,
    loyaltyRegistrationModal: {
      ...state.loyaltyRegistrationModal,
      createLoyaltyRequest: {
        ...state.loyaltyRegistrationModal.checkPhoneVerifyRequest,
        loading: true,
      },
    },
  }));

  store.on(CREATE_LOYALTY_REQUEST.success, (state, { cardNumber, keycardType }) => ({
    ...state,
    loyaltyRegistrationModal: {
      ...state.loyaltyRegistrationModal,
      createLoyaltyRequest: {
        ...state.loyaltyRegistrationModal.createLoyaltyRequest,
        loading: false,
      },
      successStep: {
        cardNumber,
        keycardType,
      },
    },
  }));

  store.on(CREATE_LOYALTY_REQUEST.failure, (state, data) => ({
    ...state,
    loyaltyRegistrationModal: {
      ...state.loyaltyRegistrationModal,
      createLoyaltyRequest: {
        ...state.loyaltyRegistrationModal.createLoyaltyRequest,
        loading: false,
        error: data,
      },
    },
  }));

  store.on(GENERATE_CODE_REQUEST.loading, (state) => ({
    ...state,
    loyaltyRegistrationModal: {
      ...state.loyaltyRegistrationModal,
      generateCodeRequest: {
        ...state.loyaltyRegistrationModal.generateCodeRequest,
        loading: true,
      },
    },
  }));

  store.on(GENERATE_CODE_REQUEST.success, (state) => ({
    ...state,
    loyaltyRegistrationModal: {
      ...state.loyaltyRegistrationModal,
      generateCodeRequest: {
        ...state.loyaltyRegistrationModal.generateCodeRequest,
        loading: false,
      },
    },
  }));

  store.on(GENERATE_CODE_REQUEST.failure, (state, data) => ({
    ...state,
    loyaltyRegistrationModal: {
      ...state.loyaltyRegistrationModal,
      generateCodeRequest: {
        ...state.loyaltyRegistrationModal.generateCodeRequest,
        loading: false,
        error: data,
      },
    },
  }));

  store.on(VERIFY_CODE_REQUEST.loading, (state) => ({
    ...state,
    loyaltyRegistrationModal: {
      ...state.loyaltyRegistrationModal,
      verifyCodeRequest: {
        ...state.loyaltyRegistrationModal.verifyCodeRequest,
        loading: true,
        error: null,
      },
    },
  }));

  store.on(VERIFY_CODE_REQUEST.success, (state) => ({
    ...state,
    loyaltyRegistrationModal: {
      ...state.loyaltyRegistrationModal,
      verifyCodeRequest: {
        ...state.loyaltyRegistrationModal.verifyCodeRequest,
        loading: false,
      },
    },
  }));

  store.on(VERIFY_CODE_REQUEST.failure, (state, data) => ({
    ...state,
    loyaltyRegistrationModal: {
      ...state.loyaltyRegistrationModal,
      verifyCodeRequest: {
        ...state.loyaltyRegistrationModal.verifyCodeRequest,
        loading: false,
        error: data,
      },
    },
  }));

  store.on(REGISTRATION_MODAL.open, (state) => ({
    ...state,
    loyaltyRegistrationModal: {
      ...state.loyaltyRegistrationModal,
      show: true,
    },
  }));

  store.on(REGISTRATION_MODAL.close, (state) => ({
    ...state,
    loyaltyRegistrationModal: {
      ...state.loyaltyRegistrationModal,
      show: false,
    },
  }));

  store.on(REGISTRATION_MODAL.setStep, (state, step) => ({
    ...state,
    loyaltyRegistrationModal: {
      ...state.loyaltyRegistrationModal,
      step,
    },
  }));

  store.on(REGISTRATION_MODAL.setForcedPhoneNumber, (state, { forcedPhoneNumber }) => ({
    ...state,
    loyaltyRegistrationModal: {
      ...state.loyaltyRegistrationModal,
      codeVerifyStep: {
        ...state.loyaltyRegistrationModal.codeVerifyStep,
        forcedPhoneNumber,
      },
    },
  }));

  store.on(REGISTRATION_MODAL.setPhoneNumber, (state, { phoneNumber }) => ({
    ...state,
    loyaltyRegistrationModal: {
      ...state.loyaltyRegistrationModal,
      codeVerifyStep: {
        ...state.loyaltyRegistrationModal.codeVerifyStep,
        phoneNumber,
      },
    },
  }));

  store.on(REGISTRATION_MODAL.setChooseKeycardData, (state, { keycardType, marketingConsent }) => ({
    ...state,
    loyaltyRegistrationModal: {
      ...state.loyaltyRegistrationModal,
      keycardChooseStep: {
        keycardType,
        marketingConsent,
      },
    },
  }));

  store.on(CHECK_PHONE_VERIFY_REQUEST.request, (state) => {
    const { orchestratorHost, useCredentials, apiKey, requestID, model, loyaltyRegistrationModal } =
      store.get();
    const regionId = model?.properties?.regionId;

    if (regionId) {
      store.dispatch(CHECK_PHONE_VERIFY_REQUEST.loading);

      checkPhoneVerify(
        { regionId },
        {
          orchestratorHost,
          useCredentials,
          apiKey,
          requestID,
        },
      )
        .then(({ needVerify, phoneNumber }) => {
          store.dispatch(CHECK_PHONE_VERIFY_REQUEST.success);

          const { keycardType, marketingConsent } = loyaltyRegistrationModal.keycardChooseStep;

          if (!needVerify) {
            store.dispatch(CREATE_LOYALTY_REQUEST.request, { keycardType, marketingConsent });
            return state;
          } else {
            store.dispatch(REGISTRATION_MODAL.setForcedPhoneNumber, { forcedPhoneNumber: phoneNumber });
            store.dispatch(REGISTRATION_MODAL.setStep, ERegistrationStep.CODE_GENERATE);
          }
        })
        .catch((e) => {
          const error = e as Error;

          store.dispatch(CHECK_PHONE_VERIFY_REQUEST.failure, error?.message);
        });
    }
  });

  store.on(CREATE_LOYALTY_REQUEST.request, (state, { keycardType, marketingConsent }) => {
    const { orchestratorHost, useCredentials, apiKey, requestID, model } = store.get();
    const regionId = model?.properties.regionId;

    if (regionId) {
      store.dispatch(CREATE_LOYALTY_REQUEST.loading);

      createLoyaltyRequest(
        { keycardType, marketingConsent, regionId },
        {
          orchestratorHost,
          useCredentials,
          apiKey,
          requestID,
        },
      )
        .then(({ cardNumber }) => {
          store.dispatch(CREATE_LOYALTY_REQUEST.success, { cardNumber, keycardType });
          store.dispatch(REGISTRATION_MODAL.setStep, ERegistrationStep.SUCCESS);
          dispatchEvent(new Event(ELoyaltyCustomEvents.LOYALTY_CREATED));
        })
        .catch((e) => {
          const error = e as Error;

          store.dispatch(CREATE_LOYALTY_REQUEST.failure, error?.message);
        });
    }
  });

  store.on(GENERATE_CODE_REQUEST.request, async (_, { phoneNumber }) => {
    const { orchestratorHost, useCredentials, apiKey, requestID, model } = store.get();
    const regionId = model?.properties.regionId;

    if (regionId) {
      store.dispatch(REGISTRATION_MODAL.setPhoneNumber, { phoneNumber });
      store.dispatch(GENERATE_CODE_REQUEST.loading);

      try {
        const result = await verificationCodeGenerate(
          { phoneNumber, regionId },
          {
            orchestratorHost,
            useCredentials,
            apiKey,
            requestID,
          },
        );

        if (!result.error) {
          store.dispatch(GENERATE_CODE_REQUEST.success);
          store.dispatch(REGISTRATION_MODAL.setStep, ERegistrationStep.CODE_SEND);
        } else {
          store.dispatch(GENERATE_CODE_REQUEST.failure, result.message);
        }
      } catch (e) {
        store.dispatch(
          GENERATE_CODE_REQUEST.failure,
          i18n.t('loyalty-registration-modal.generate-phone-step.errors.internal'),
        );
      }
    }
  });

  store.on(VERIFY_CODE_REQUEST.request, async (_, { phoneNumber, code }) => {
    const { orchestratorHost, useCredentials, apiKey, requestID, model, loyaltyRegistrationModal } =
      store.get();
    const regionId = model?.properties.regionId;
    const keycardType = loyaltyRegistrationModal.keycardChooseStep.keycardType;
    const marketingConsent = loyaltyRegistrationModal.keycardChooseStep.marketingConsent;

    if (regionId) {
      store.dispatch(VERIFY_CODE_REQUEST.loading);

      try {
        const result = await verificationCodeVerify(
          { phoneNumber, verificationCode: code, regionId },
          {
            orchestratorHost,
            useCredentials,
            apiKey,
            requestID,
          },
        );

        if (!result.error) {
          store.dispatch(VERIFY_CODE_REQUEST.success);
          store.dispatch(CREATE_LOYALTY_REQUEST.request, { keycardType, marketingConsent });
        } else {
          store.dispatch(VERIFY_CODE_REQUEST.failure, result.message);
        }
      } catch (e) {
        store.dispatch(
          VERIFY_CODE_REQUEST.failure,
          i18n.t('loyalty-registration-modal.verify-code-step.errors.internal'),
        );
      }
    }
  });
};

export enum EKeycardType {
  HOME = 'HOME',
  PRO = 'PRO',
}

export type LoyaltyRegistrationModalStore = {
  loyaltyRegistrationModal: {
    show: boolean;
    step: ERegistrationStep;
    checkPhoneVerifyRequest: {
      loading: boolean;
      error: string | null;
    };
    createLoyaltyRequest: {
      loading: boolean;
      error: string | null;
    };
    generateCodeRequest: {
      loading: boolean;
      error: string | null;
    };
    verifyCodeRequest: {
      loading: boolean;
      error: string | null;
    };
    keycardChooseStep: {
      marketingConsent: boolean;
      keycardType: EKeycardType | null;
    };
    successStep: {
      keycardType: EKeycardType | null;
      cardNumber: string | null;
    };
    codeVerifyStep: {
      phoneNumber: string | null;
      forcedPhoneNumber: string | null;
    };
  };
};
